import { format, parseISO } from 'date-fns';

// Format date for display (e.g., "Mar 15, 2024")
export const formatDateForDisplay = (dateString) => {
  if (!dateString) return '';
  try {
    return format(new Date(dateString), 'MMM d, yyyy');
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

// Format time for display (e.g., "2:00 PM")
export const formatTimeForDisplay = (timeString) => {
  if (!timeString) return '';
  
  try {
    // Parse the HH:MM time
    const [hoursStr, minutesStr] = timeString.split(':');
    let hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);
    
    // Convert to 12-hour format with AM/PM
    const period = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12; // Convert 0 to 12 for 12 AM
    
    return `${hours}:${minutes.toString().padStart(2, '0')} ${period}`;
  } catch (error) {
    console.error('Error formatting time for display:', error);
    return timeString;
  }
};

// Get time value from various formats
export const getTimeValue = (timeString, dateObject) => {
  if (!timeString && !dateObject) return null;
  
  // First try to parse the time string
  if (timeString) {
    // Handle HH:MM format
    if (/^\d{1,2}:\d{2}$/.test(timeString)) {
      return timeString;
    }
    
    // Handle AM/PM formats
    if (/^\d{1,2}(?::\d{2})?\s*[AP]M$/i.test(timeString)) {
      try {
        const [time, period] = timeString.split(/\s+/);
        const [hours, minutes = '00'] = time.split(':');
        let hour = parseInt(hours, 10);
        
        // Convert to 24-hour format
        if (period.toUpperCase() === 'PM' && hour < 12) hour += 12;
        if (period.toUpperCase() === 'AM' && hour === 12) hour = 0;
        
        return `${hour.toString().padStart(2, '0')}:${minutes}`;
      } catch (error) {
        console.error('Error parsing AM/PM time:', error);
      }
    }
  }
  
  // If no valid time string, try to extract from date object
  if (dateObject) {
    try {
      const date = new Date(dateObject);
      if (!isNaN(date.getTime())) {
        return format(date, 'HH:mm');
      }
    } catch (error) {
      console.error('Error extracting time from date:', error);
    }
  }
  
  return null;
};

// Combine date and time into ISO string
export const combineDateAndTime = (dateStr, timeStr) => {
  if (!dateStr) return null;
  
  try {
    const [year, month, day] = dateStr.split('-').map(Number);
    const [hours, minutes] = timeStr ? timeStr.split(':').map(Number) : [12, 0];
    
    // Create date in local timezone
    const date = new Date(year, month - 1, day, hours, minutes);
    
    // Convert to ISO string
    return date.toISOString();
  } catch (error) {
    console.error('Error combining date and time:', error);
    return null;
  }
};

export const formatDateTime = (dateString, timeString) => {
  if (!dateString) return ''
  const date = formatDateForDisplay(dateString)
  const time = timeString ? formatTimeForDisplay(timeString) : ''
  return time ? `${date} at ${time}` : date
}

export const formatReservationDate = (
  originalDate,
  overrideDate,
  originalTime,
  overrideTime,
  hasOverride
) => {
  // If no override or no override values, just show original
  if (!hasOverride || (!overrideDate && !overrideTime)) {
    return formatDateTime(originalDate, originalTime)
  }

  // Format helpers
  const origDateStr = formatDateForDisplay(originalDate)
  const overrideDateStr = overrideDate ? formatDateForDisplay(overrideDate) : origDateStr
  const origTimeStr = formatTimeForDisplay(originalTime)
  const overrideTimeStr = overrideTime ? formatTimeForDisplay(overrideTime) : origTimeStr

  // Compare date and time
  const isDateChanged = overrideDate && (overrideDateStr !== origDateStr)
  const isTimeChanged = overrideTime && (overrideTimeStr !== origTimeStr)

  // If nothing actually changed, show original
  if (!isDateChanged && !isTimeChanged) {
    return formatDateTime(originalDate, originalTime)
  }

  // If both changed
  if (isDateChanged && isTimeChanged) {
    return (
      <span>
        <span className="line-through text-gray-400">{origDateStr} at {origTimeStr}</span>{' '}
        <span className="text-blue-600 font-medium">{overrideDateStr} at {overrideTimeStr}</span>
        <span className="text-xs text-gray-500 ml-1">(Changed)</span>
      </span>
    )
  }

  // If only date changed
  if (isDateChanged) {
    return (
      <span>
        <span className="line-through text-gray-400">{origDateStr}</span>{' '}
        <span className="text-blue-600 font-medium">{overrideDateStr}</span> at {origTimeStr}
        <span className="text-xs text-gray-500 ml-1">(Changed)</span>
      </span>
    )
  }

  // If only time changed
  if (isTimeChanged) {
    return (
      <span>
        {origDateStr} at <span className="line-through text-gray-400">{origTimeStr}</span>{' '}
        <span className="text-blue-600 font-medium">{overrideTimeStr}</span>
        <span className="text-xs text-gray-500 ml-1">(Changed)</span>
      </span>
    )
  }

  // Fallback
  return formatDateTime(originalDate, originalTime)
} 